// What every card the advisor writes has in common (Phase 4.5).
//
// Three kinds of card — a comment, a note, the evaluation — and one shell, so
// they behave the same way and there is one place to change how they do. Each
// arrives shut, showing a single sentence; the card itself opens it and the
// trash puts it away.
//
// Shut by default because the advisor now says more per card than it used to,
// and everything it says is worth reading *once you have decided the headline
// is worth reading*. The one sentence is the decision; the rest is the answer.
//
// The whole card is the control. A card is one thing and says one thing, so
// there is no part of it that should mean "not this" — a small triangle in the
// corner asked the reader to aim at a 34px target to do what tapping the
// sentence obviously ought to do. The triangle stays as a drawing, so a shut
// card still looks like it has more in it, but it is not what you press.

import { type MouseEvent, type ReactNode, useCallback, useId, useState } from "react";

import { Icon } from "../ui/Icon";
import { IconButton } from "../ui/TopBar";

interface Props {
  /** For the card's own class, and the label the controls announce. */
  kind: string;
  /** The sentence a shut card shows. */
  summary: ReactNode;
  /** Anything to the left of the controls: a type badge, a state word. */
  badge?: ReactNode;
  /** Everything below the summary, rendered only while the card is open. */
  children?: ReactNode;
  /** The row of controls along the bottom, open or shut. */
  actions?: ReactNode;
  className?: string;
  onDismiss?: () => void;
}

/** Things that are already something to press, or to read into. A click that
 *  lands on one of these belongs to it, not to the card. */
const OWN_CLICK = "button, a, input, select, textarea, label, summary, [contenteditable]";

export function Card({ kind, summary, badge, children, actions, className, onDismiss }: Props) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const toggle = useCallback(() => setOpen((o) => !o), []);

  // On the article rather than on each part of it, so there is no dead strip
  // between the badge and the sentence. It runs in the bubble phase, after any
  // inner button has had the event, which is why nothing inside a card has to
  // stop propagation to keep working.
  const onCardClick = (e: MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement | null)?.closest(OWN_CLICK)) return;
    // Dragging across a sentence to copy it ends in a click on the card. Shutting
    // the card on the reader mid-selection is the one way this gesture feels broken.
    if ((window.getSelection()?.toString() ?? "") !== "") return;
    toggle();
  };

  const classes = ["advice", kind];
  if (open) classes.push("open");
  if (className) classes.push(className);

  return (
    <article className={classes.join(" ")} onClick={onCardClick}>
      <header className="advice-head">
        {/* The keyboard's way in, and the whole header row is its target. The
            article's click handler covers the pointer; this covers Tab. */}
        <button
          type="button"
          className="advice-toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={toggle}
        >
          {badge}
          <Icon name="triangle" size={14} />
          <span className="sr-only">{open ? `Contract this ${kind}` : `Expand this ${kind}`}</span>
        </button>
        {onDismiss && (
          <IconButton
            icon="trash"
            label={`Put this ${kind} away`}
            size={22}
            onClick={onDismiss}
          />
        )}
      </header>

      {/* A div rather than a paragraph: the evaluation's summary is a list
          and a bar, and a <p> may not contain either — the browser would
          reparent them and the card would come apart. */}
      <div className="advice-summary">{summary}</div>

      {/* The wrapper is always here, empty or not, so aria-controls points at
          something that exists whichever way the card is standing. */}
      <div id={bodyId}>{open && children}</div>
      {actions}
    </article>
  );
}

/** A labelled disclosure inside an open card: the reasoning, the notes.
 *
 *  Its own control rather than part of the card's, because these are the two
 *  things a reader wants only sometimes, and putting them behind the same
 *  gesture as the rest would mean opening a card to see everything or
 *  nothing.
 */
export function Reveal({
  label,
  count,
  children,
}: {
  label: string;
  count?: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="reveal">
      <button
        type="button"
        className={`reveal-button${open ? " open" : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="triangle" size={14} />
        {count === undefined ? label : `${label} (${count})`}
      </button>
      {open && <div className="reveal-body">{children}</div>}
    </div>
  );
}
